import React from 'react'
import { useParams } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import AdminPage from '../../shared/AdminPage'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import { ActionField, ColorField } from '../TagDetails/TagOptionList'
import { useClassificationTags, useClassificationTagTypes } from '../../../../../api/coreData'
import {
  useCreateAssetTag,
  useDeleteAssetTag,
  useModifyAssetTag
} from '../../../../../api/dataMaintenance'
import SydButton from '../../../../commonDesign/Button'
import ConfirmationModal from '../../../../molecules/ConfirmationModal'
import ErrorDisplay from '../../../../molecules/ErrorDisplay'
import ProductTagModal from './ProductTagModal'

const useStyles = makeStyles((theme) => ({
  description: {
    fontWeight: '400',
    marginTop: '0px'
  },
  detailSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end'
  }
}))

const ProductTagDetails = () => {
  const classes = useStyles()
  const params = useParams()
  const tagId = parseInt(params.tagId, 10)
  const {
    data,
    isLoading,
    isError,
    error
  } = useClassificationTags({ includes: { assetCount: true }, filters: { classificationTagTypeId: tagId } })
  const {
    data: tagDetailDataRaw,
    isLoading: tagDetailLoading
  } = useClassificationTagTypes({ filters: { classificationTagTypeId: tagId } })
  const tagDetailData = tagDetailDataRaw?.[0]
  const { mutateAsync: deleteProductTag, isLoading: deleteLoading } = useDeleteAssetTag()
  const { mutateAsync: updateProductTag, isLoading: updateLoading } = useModifyAssetTag()
  const { mutateAsync: createProductTag, isLoading: createLoading } = useCreateAssetTag()
  const [tag, setTag] = React.useState(null)
  const [editModalOpen, setEditModalOpen] = React.useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const openModal = (row) => {
    setTag(row.original)
    setEditModalOpen(true)
  }
  const modalSubmit = async (tag) => {
    let result
    if (tag.id) {
      result = await updateProductTag(tag)
    } else {
      result = await createProductTag(tag)
    }
    if (result.success) {
      setEditModalOpen(false)
    }
  }
  const defaultColumnConfig = {
    columns: [
      { Header: 'Id', accessor: 'classificationTagId', type: 'number' },
      { Header: 'Tag Name', accessor: 'longName' },
      {
        Header: 'Ordinal',
        accessor: 'ordinal',
        alignRight: true
      },
      {
        Header: 'Color',
        accessor: 'colorField',
        Cell: ({ row }) => (ColorField({ hex: row.original.colorField })),
        width: 90
      },
      {
        Header: 'Count of assets assigned',
        accessor: 'assetCount',
        alignRight: true
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => ActionField({
          group: row.original,
          editFunction: () => {
            openModal(row)
          },
          deleteFunction: () => {
            setTag(row.original)
            setDeleteModalOpen(true)
          },
          canDelete: row.original.assetCount <= 0,
          noDeleteReason: 'Tag with assets cannot be deleted'
        })
      }
    ],
    defaultSort: [{ id: 'classificationTagId', desc: false }]
  }

  if (tagDetailLoading) {
    return <Loading />
  }
  return (
    <AdminPage>
      <ConfirmationModal
        open={deleteModalOpen || deleteLoading}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          deleteProductTag(tag)
          setDeleteModalOpen(false)
        }}
        isLoading={deleteLoading}
        confirmationType='delete'
        confirmationText={`Are you sure you want to delete ${tag?.longName} tag?`}
        buttonLabelCancel='Cancel'
        buttonLabelConfirm='Delete'
      />
      <ProductTagModal open={editModalOpen} close={() => setEditModalOpen(false)} assetTag={tag} assetTagType={tagDetailData} submit={modalSubmit} isLoading={updateLoading || createLoading} />
      <h1 className='text-2xl font-bold mb-0'>
        {tagDetailData?.longName} - #{tagDetailData?.classificationTagTypeId}
      </h1>
      <div className={classes.detailSection}>
        <div>
          <p className={classes.description}>
            Code: {tagDetailData?.shortName}
            <br />
            Classification Type: {tagDetailData?.classificationType}
          </p>
        </div>
        <div>
          <SydButton onClick={() => openModal({ original: {} })}>New Tag</SydButton>
        </div>
      </div>

      <hr />
      {
        isLoading ? (
          <Loading />
        ) : data?.length > 0 ? (
          <OperationalTable.Wrapper>
            <OperationalTable
              variant='v2'
              mode='auto'
              itemName='Product Tags'
              columns={defaultColumnConfig.columns}
              defaultSort={defaultColumnConfig.defaultSort}
              data={data || []}
              total={data?.length}
              isLoading={isLoading}
              isError={isError}
              error={error}
            />
          </OperationalTable.Wrapper>
        ) : (
          <ErrorDisplay error='No associated tags.' soft />
        )
      }

    </AdminPage>
  )
}

export default ProductTagDetails
